// Create Competition within a League
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLeague } from '../contexts/LeagueContext';
import { Button, Input, Card } from '../components';
import { createCompetition, createPlayer, isAnonymous, createPlayerFromLeagueMember } from '../services';
import { createPlayerToken } from '../services/tokenService';
import { storeToken } from '../utils/localStorage';
import { generatePin } from '../utils/crypto';
import { getTodayDateString } from '../utils/dateHelpers';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getLeague } from '../services/leagueService';

const LeagueCreateCompetition = () => {
  const navigate = useNavigate();
  const { leagueId } = useParams();
  const { authUid, user } = useAuth();
  const { currentLeague, loadLeague, isLeagueAdmin } = useLeague();

  // Load league data
  useEffect(() => {
    if (leagueId) {
      loadLeague(leagueId);
    }
  }, [leagueId, loadLeague]);

  // Check if user is anonymous and redirect to account creation
  useEffect(() => {
    if (isAnonymous()) {
      navigate('/create-account', {
        state: {
          returnTo: `/league/${leagueId}/create-competition`,
          requireAccount: true
        }
      });
    }
  }, [navigate, leagueId]);

  // Helper to add days to a date string
  const addDaysToDate = (dateString, days) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [endDate, setEndDate] = useState(addDaysToDate(getTodayDateString(), 4));
  const [maxPlayers, setMaxPlayers] = useState(20);
  const [rankingMethod, setRankingMethod] = useState('byCategory');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [generatedPin, setGeneratedPin] = useState('');
  const [competitionId, setCompetitionId] = useState('');

  // Pre-fill display name from user
  useEffect(() => {
    if (user?.displayName) {
      setDisplayName(user.displayName);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate dates
      if (endDate && startDate > endDate) {
        setError('End date must be after start date');
        setLoading(false);
        return;
      }

      // Create organizer player first (with a temporary competition ID)
      const pin = generatePin(6);

      // We'll create the player after competition, but we need to generate IDs first
      const { v4: uuidv4 } = await import('uuid');
      const playerId = uuidv4();

      // Create competition with the player ID and link to league
      const competition = await createCompetition(
        {
          name,
          startDate,
          endDate: endDate || startDate,
          maxPlayers: parseInt(maxPlayers),
          rankingMethod,
          timezone: 'Europe/Oslo',
          leagueId // Link to league
        },
        playerId // Set organizerId to the player we're about to create
      );

      // Now create the player with the pre-generated playerId and link to auth account
      const player = await createPlayer(competition.id, displayName, pin, playerId, authUid);

      // Create token
      const tokenId = await createPlayerToken(competition.id, player.id, authUid);
      storeToken(competition.id, tokenId);

      // Update the competition to ensure leagueId is set
      const compRef = doc(db, 'competitions', competition.id);
      await updateDoc(compRef, {
        leagueId: leagueId,
        updatedAt: serverTimestamp()
      });

      // Fetch fresh league data to ensure we have all members
      // (currentLeague from context might be stale or not loaded)
      console.log('=== Adding League Members ===');
      const freshLeague = await getLeague(leagueId);
      console.log('Fresh league data:', freshLeague);
      console.log('Fresh league members:', freshLeague?.members);
      console.log('authUid:', authUid);

      if (freshLeague?.members && freshLeague.members.length > 0) {
        console.log(`Found ${freshLeague.members.length} league members`);
        for (const member of freshLeague.members) {
          console.log(`Processing member: ${member.displayName}, odplayerId: ${member.odplayerId}`);
          // Skip the organizer (already added above)
          if (member.odplayerId === authUid) {
            console.log(`  Skipping ${member.displayName} (is organizer)`);
            continue;
          }

          try {
            console.log(`  Creating player for ${member.displayName}...`);
            const newPlayer = await createPlayerFromLeagueMember(
              competition.id,
              member.displayName,
              member.odplayerId
            );
            console.log(`  Created player: ${newPlayer?.id || 'null (name taken?)'}`);
          } catch (memberErr) {
            // Log but don't fail the whole process if one member fails
            console.error(`Failed to add member ${member.displayName}:`, memberErr);
          }
        }
      } else {
        console.log('No league members found or league is null');
      }

      setGeneratedPin(pin);
      setCompetitionId(competition.id);

      // If user has an account (not anonymous), skip PIN screen and go directly to competition
      if (user?.email) {
        navigate(`/competition/${competition.id}`);
      } else {
        setShowPin(true);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error creating competition:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate(`/competition/${competitionId}`);
  };

  // Check if user is admin
  if (currentLeague && !isLeagueAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">Only league admins can create competitions.</p>
          <Button onClick={() => navigate(`/league/${leagueId}`)}>Back to League</Button>
        </Card>
      </div>
    );
  }

  if (showPin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full">
          <Card>
            <h2 className="text-2xl font-bold mb-4 text-center">Competition Created!</h2>
            <p className="text-center text-gray-600 mb-4">
              Added to <span className="font-semibold">{currentLeague?.name}</span>
            </p>
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
              <p className="text-sm text-gray-700 mb-2 text-center">Your PIN:</p>
              <p className="text-3xl font-bold text-center text-gray-900 tracking-widest">{generatedPin}</p>
            </div>
            <p className="text-sm text-red-600 mb-4 text-center font-semibold">
              Save this PIN! You'll need it to access this competition from other devices.
            </p>
            <Button onClick={handleContinue} fullWidth>
              Continue to Competition
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Button variant="outline" size="sm" onClick={() => navigate(`/league/${leagueId}`)}>
            ← Back to League
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Create Competition</h1>
          {currentLeague && (
            <p className="mt-2 text-gray-600">
              In league: <span className="font-semibold">{currentLeague.name}</span>
            </p>
          )}
        </div>

        <Card>
          <form onSubmit={handleSubmit}>
            <Input
              label="Competition Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Summer Games 2025"
              required
            />

            <Input
              label="Your Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              required
            />

            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setStartDate(newStartDate);
                setEndDate(addDaysToDate(newStartDate, 4));
              }}
              required
            />

            <Input
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
            />

            <Input
              label="Max Players"
              type="number"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(e.target.value)}
              min="2"
              max="100"
              required
            />

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ranking Method
              </label>
              <select
                value={rankingMethod}
                onChange={(e) => setRankingMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="byCategory">By Category</option>
                <option value="byDay">By Day</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {rankingMethod === 'byCategory'
                  ? 'Players ranked by total placement points across all categories'
                  : 'Players ranked by daily placement points, calculated from category performance each day'}
              </p>
            </div>

            {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Creating...' : 'Create Competition'}
            </Button>

            <button
              type="button"
              onClick={() => navigate(`/league/${leagueId}`)}
              className="w-full mt-3 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default LeagueCreateCompetition;
